"use client";
import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import { useState } from "react";
import Image from "next/image";
import Button from "@/Components/Button";
import { toWebp } from "@/utils/common";

interface RequestProposalProps {
  data?: any;
}

const Promisis = ({ data }: RequestProposalProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    initial: 0,
    loop: true,
    slides: {
      perView: 3, // default (desktop/tablet)
      spacing: 16,
    },
    breakpoints: {
      "(max-width: 768px)": {
        slides: {
          perView: 1,
          spacing: 8,
        },
      },
    },
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel);
    },
    created() {
      setLoaded(true);
    },
  });

  const slides = data?.cards || [];

  const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL ?? "";

  return (
    <section className="relative bg-background-white text-primary-text py-[56px] xl:py-25 md:py-[80px] overflow-hidden">
      <div className="container mx-auto px-4 text-center relative z-10">
        {/* Title */}
        <h2 className="text-heading-1 font-bold text-primary-text mb-[10px] max-w-[800px] mx-auto w-full text-center">
          {data?.title}
        </h2>

        <p className="text-subtitle-2 font-normal text-secondary-text xl:mb-[60px] mb-[32px] max-w-[940px] mx-auto">
          {data?.short_title}
        </p>

        <div className="w-full">
          {/* Slider */}
          <div ref={sliderRef} className="keen-slider w-full">
            {slides.map((slide: any, index: number) => (
              <div key={index} className="keen-slider__slide w-full group">
                <div className="w-full p-[40px_20px] h-[422px] flex flex-col items-center justify-start bg-contain bg-no-repeat bg-bottom bg-[url(/images/about/bg-promise.png)] xl:bg-[url(/images/about/bg-pm-xl.png)] group-hover:bg-[url(/images/about/promise.png)]">
                  <div className="flex flex-col items-center justify-center">
                    {slide?.image?.data?.attributes?.url && (
                      <Image
                        height={62}
                        width={62}
                        src={toWebp(baseUrl + slide?.image?.data?.attributes?.url)}
                        alt={slide?.title || "icon"}
                        className="w-[62px] h-[62px] object-contain xl:block hidden group-hover:hidden"
                      />
                    )}
                    {slide?.hover_image?.data?.attributes?.url && (

                      <Image
                        height={62}
                        width={62}
                        src={toWebp(baseUrl + slide?.hover_image?.data?.attributes?.url)}
                        alt={slide?.title || "icon"}
                        className="w-[62px] h-[62px] object-contain xl:hidden block group-hover:xl:block "
                      />
                    )}
                  </div>
                  <p className="mt-[24px] min-h-[104px] text-primary-text text-balance xl:w-[70%] flex items-center text-heading-3 font-bold text-center">
                    {slide?.title}
                  </p>
                  <p className="mt-[24px] text-secondary-text text-balance text-subtitle-2 font-normal text-center">
                    {slide?.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Arrows */}
          <div className="hidden sm:block">
            {loaded && instanceRef.current && (
              <div className="flex items-center justify-center gap-3 mt-[40px]">
                {/* Left Arrow */}
                <button
                  onClick={() => instanceRef.current?.prev()}
                  className="text-xl font-bold w-[40px] h-[40px] border-1 border-[#040405] flex items-center justify-center text-primary-text hover:text-black"
                >
                  <svg width="25" height="24" viewBox="0 0 25 24" fill="none">
                    <path
                      d="M10.0703 5.93018L4.00031 12.0002L10.0703 18.0702"
                      stroke="#040405"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M21 12L4.17 12"
                      stroke="#040405"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                {/* Slide Count */}
                <span className="text-subtitle-1 font-bold text-primary-text">
                  {currentSlide + 1}/{slides.length}
                </span>

                {/* Right Arrow */}
                <button
                  onClick={() => instanceRef.current?.next()}
                  className="text-xl font-bold w-[40px] h-[40px] border-1 border-[#040405] flex items-center justify-center text-primary-text hover:text-black"
                >
                  <svg width="25" height="24" viewBox="0 0 25 24" fill="none">
                    <path
                      d="M14.9297 5.93018L20.9997 12.0002L14.9297 18.0702"
                      stroke="#040405"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M4 12L20.83 12"
                      stroke="#040405"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Dots for Mobile */}
        <div className="flex sm:hidden justify-center gap-2 mt-[24px]">
          {slides.map((_: any, idx: number) => (
            <button
              key={idx}
              onClick={() => instanceRef.current?.moveToIdx(idx)}
              className={`w-[16px] h-[4px] rounded-full transition-all ${currentSlide === idx
                ? "bg-blue-100 w-[140px] bg-gradient-to-r from-[#8ABC00] to-[#0086E6]"
                : "bg-blue-100 hover:bg-blue-500"
                }`}
            />
          ))}
        </div>

        {/* CTA Button */}
        {data?.cta?.label && data?.cta?.href && (
          <a href={data?.cta?.href} className="inline-block">
            <Button label={data?.cta?.label} className="sm:mt-[16px] mt-[32px]" />
          </a>
        )}
      </div>
    </section>
  );
};

export default Promisis;
